import { InfoCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import {
  type IAdminProduct,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICurrencyType,
  type IProductFeatureFlag,
  type IProductStocks,
  type IProductWarehouseShort,
  type IWarehouse,
  type IWarehouseDetail,
} from '@mahawi/eshop-common/src/types';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { ColumnFilterItem } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { type Key } from 'react';

import ExternalLink from '../components/external-link';
import Link from '../components/link';
import StatusIcon from '../components/status-icon';
import { type IConfigState } from '../reducers/config/types';
import { type ICurrencyState } from '../reducers/currency/types';
import { type IFeatureFlagsState } from '../reducers/feature-flags/types';
import { type ILanguageState } from '../reducers/language/types';
import { type IProductsState } from '../reducers/products/types';
import { type IWarehouseState } from '../reducers/warehouse/types';
import { SPACING } from '../theme/tokens';
import { coloredRenderOfNumberInTable } from './colored-render-of-number-in-table';
import { EMPTY_VALUE } from './empty-value';
import { featureFlagTags } from './feature-flag-tags';
import { getTableFeatureFlagsColumnFilters } from './table-feature-flags-column';
import { getColumnSearch } from './table-get-column-search';
import {
  getCalculatedEndPriceMarginColumn,
  type ProductTableColumnCalculatedEndPriceMargin,
} from './table-row-product-calculated-end-price-margin';

const { Text } = Typography;
dayjs.extend(relativeTime);

function getAttractivenessColor(score: number): string {
  if (score >= 70) {
    return 'green';
  }

  if (score >= 40) {
    return 'yellow';
  }

  return 'red';
}

export interface ProductTableColumn
  extends IAdminProduct, ProductTableColumnCalculatedEndPriceMargin {
  key: string;
  name: string;
  priceEUR: ICurrencyPrice | undefined;
}

export function getDataSourceForProductsTable({
  Products,
  Language,
}: {
  Products: IProductsState;
  Language: ILanguageState;
}): ProductTableColumn[] {
  if (!Products.products) {
    return [];
  }

  const dataSource: ProductTableColumn[] = Products.products.map(
    ({
      brand,
      gtin,
      stocks,
      mpn,
      names,
      uuid,
      photosCount,
      relatedProductsCount,
      prices,
      primaryWarehouse,
      featureFlags,
      categoriesCount,
      isActive,
      endPriceMarginRate,
      calculatedEndPriceMarginPercentage,
      calculatedEndPriceMarginEur,
      calculatedEndPriceAt,
      productWarehouses,
      attractivenessScore,
    }: IAdminProduct): ProductTableColumn => {
      const productLocaNameLocalized: string | null = getTranslation(
        names,
        Language.languageType,
      );

      const priceEUR: ICurrencyPrice | undefined = prices.find(
        (p: ICurrencyPrice): boolean => p.isoCode === 'EUR',
      );

      return {
        key: uuid,
        brand,
        name: productLocaNameLocalized || EMPTY_VALUE,
        gtin,
        stocks,
        mpn,
        uuid,
        photosCount,
        relatedProductsCount,
        priceEUR,
        primaryWarehouse,
        featureFlags,
        categoriesCount,
        names,
        prices,
        isActive,
        endPriceMarginRate,
        calculatedEndPriceMarginPercentage,
        calculatedEndPriceMarginEur,
        calculatedEndPriceAt,
        productWarehouses,
        attractivenessScore,
      };
    },
  );

  return dataSource;
}

export function getProductsTableColumns({
  dataSource,
  Config,
  Currency,
  Language,
  FeatureFlags,
  Warehouse,
}: {
  dataSource: ProductTableColumn[];
  Config: IConfigState;
  Currency: ICurrencyState;
  Language: ILanguageState;
  FeatureFlags: IFeatureFlagsState;
  Warehouse: IWarehouseState;
}): ColumnsType<object> {
  const currencyTypeEUR: ICurrencyType | undefined = Currency.currencies.find(
    (c: ICurrencyType): boolean => c.isoCode === 'EUR',
  );

  if (!currencyTypeEUR) {
    throw new Error('EUR currency not found');
  }

  const columnFilterItemPrimaryWarehouse: ColumnFilterItem[] = [
    ...new Set(
      dataSource.map(
        ({ primaryWarehouse }: ProductTableColumn): string | undefined =>
          primaryWarehouse?.name,
      ),
    ),
  ]
    .filter(notEmpty)
    .map((name: string) => ({
      text: name,
      value: name,
    }));

  columnFilterItemPrimaryWarehouse.push({
    text: 'With primary warehouse',
    value: 'withPrimaryWarehouse',
  });

  columnFilterItemPrimaryWarehouse.push({
    text: 'Without primary warehouse',
    value: 'withoutPrimaryWarehouse',
  });

  const columns: ColumnsType<object> = [
    {
      title: 'Brand',
      dataIndex: ['brand', 'name'],
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        stringCompare(a.brand?.name, b.brand?.name),
      filterSearch: true,
      filters: [
        ...new Set(
          dataSource.map(
            ({ brand }: ProductTableColumn): string | undefined => brand?.name,
          ),
        ),
      ]
        .filter(notEmpty)
        .map((name: string) => ({
          text: name,
          value: name,
        }))
        .sort((a, b): number => stringCompare(a.value, b.value)),
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean =>
        record.brand?.name === value,
      width: '5%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record: ProductTableColumn): React.ReactElement => (
        <Space align="start" size={SPACING.sm}>
          <Link to={`/product/${record.uuid}`} label={text} openInNewTab />
          <ExternalLink
            to={`${
              Config.url
            }/${getLowerCaseLanguageCode(Language.languageType)}/product/${record.uuid}`}
            ariaLabel="Open product in new tab"
          />
        </Space>
      ),
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        stringCompare(a.name, b.name),
      width: '20%',
      ...getColumnSearch('name'),
    },
    {
      title: (
        <>
          <span>
            Price EUR
            <br />
            <small>without TAX</small>
          </span>
        </>
      ),

      dataIndex: 'priceEUR',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        numberCompare(a.priceEUR?.priceWithoutTax, b.priceEUR?.priceWithoutTax),
      render: (
        priceEUR: ICurrencyPrice | undefined,
        record: ProductTableColumn,
      ): React.ReactElement => {
        const prices: number[] = record.prices
          .map(({ pricesWithTax }: ICurrencyPrice): number[] =>
            pricesWithTax.map(
              ({ price }: ICountryCurrencyPrice): number => price,
            ),
          )
          .flat();
        const allValid: boolean =
          prices.every((price: number): boolean => price > 0) &&
          prices.length > 0;

        return (
          <Space>
            <Text>
              {priceEUR && priceEUR.priceWithoutTax
                ? formatCurrency(priceEUR.priceWithoutTax, currencyTypeEUR)
                : EMPTY_VALUE}
            </Text>
            <StatusIcon ok={allValid} />
          </Space>
        );
      },
      filterSearch: false,
      filters: [
        {
          text: 'With price',
          value: 'withPrice',
        },
        {
          text: 'Without price',
          value: 'withoutPrice',
        },
        {
          text: 'With price TAX',
          value: 'withPriceTax',
        },
        {
          text: 'Without price TAX',
          value: 'withoutPriceTax',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withPrice') {
          return !!record.priceEUR?.priceWithoutTax;
        }

        if (value === 'withoutPrice') {
          return !record.priceEUR?.priceWithoutTax;
        }

        if (value === 'withPriceTax') {
          return (
            (record.priceEUR?.pricesWithTax.length || 0) > 0 &&
            (record.priceEUR?.pricesWithTax.every(
              (p: ICountryCurrencyPrice): boolean => p.price > 0,
            ) ||
              false)
          );
        }

        if (value === 'withoutPriceTax') {
          return (
            (record.priceEUR?.pricesWithTax.length || 0) === 0 ||
            record.priceEUR?.pricesWithTax.some(
              (p: ICountryCurrencyPrice): boolean => p.price <= 0,
            ) ||
            false
          );
        }

        return false;
      },
      width: '10%',
    },
    getCalculatedEndPriceMarginColumn(currencyTypeEUR),
    {
      title: 'Categories',
      dataIndex: 'categoriesCount',
      render: coloredRenderOfNumberInTable,
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        numberCompare(a.categoriesCount, b.categoriesCount),
      filterSearch: false,
      filters: [
        {
          text: 'With category',
          value: 'withCategory',
        },
        {
          text: 'Without category',
          value: 'withoutCategory',
        },
        {
          text: 'With one category',
          value: 'withOneCategory',
        },
        {
          text: 'With two to five categories',
          value: 'withTwoToFiveCategories',
        },
        {
          text: 'With more than five categories',
          value: 'withMoreThanFiveCategories',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withCategory') {
          return record.categoriesCount > 0;
        }

        if (value === 'withoutCategory') {
          return record.categoriesCount === 0;
        }

        if (value === 'withOneCategory') {
          return record.categoriesCount === 1;
        }

        if (value === 'withTwoToFiveCategories') {
          return record.categoriesCount > 1 && record.categoriesCount <= 5;
        }

        if (value === 'withMoreThanFiveCategories') {
          return record.categoriesCount > 5;
        }

        return false;
      },
      width: '5%',
    },
    {
      title: 'Photos',
      dataIndex: 'photosCount',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        numberCompare(a.photosCount, b.photosCount),
      filterSearch: false,
      filters: [
        {
          text: 'With photos',
          value: 'withPhotos',
        },
        {
          text: 'Without photos',
          value: 'withoutPhotos',
        },
        {
          text: 'With one photo',
          value: 'withOnePhoto',
        },
        {
          text: 'With two to five photos',
          value: 'withTwoToFivePhotos',
        },
        {
          text: 'With more than five photos',
          value: 'withMoreThanFivePhotos',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withPhotos') {
          return record.photosCount > 0;
        }

        if (value === 'withoutPhotos') {
          return record.photosCount === 0;
        }

        if (value === 'withOnePhoto') {
          return record.photosCount === 1;
        }

        if (value === 'withTwoToFivePhotos') {
          return record.photosCount > 1 && record.photosCount <= 5;
        }

        if (value === 'withMoreThanFivePhotos') {
          return record.photosCount > 5;
        }

        return false;
      },
      render: (photoCount: number): number => photoCount,
      width: '5%',
    },
    {
      title: 'Attractiveness',
      dataIndex: 'attractivenessScore',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        numberCompare(a.attractivenessScore, b.attractivenessScore),
      filterSearch: false,
      filters: [
        { text: 'With score', value: 'withScore' },
        { text: 'Without score', value: 'withoutScore' },
        { text: 'High (≥ 70)', value: 'high' },
        { text: 'Medium (≥ 40)', value: 'medium' },
        { text: 'Low (< 40)', value: 'low' },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withScore') return record.attractivenessScore != null;
        if (value === 'withoutScore') return record.attractivenessScore == null;
        if (value === 'high') return (record.attractivenessScore ?? -1) >= 70;
        if (value === 'medium')
          return (
            (record.attractivenessScore ?? -1) >= 40 &&
            (record.attractivenessScore ?? -1) < 70
          );
        if (value === 'low')
          return (
            record.attractivenessScore != null &&
            record.attractivenessScore < 40
          );
        return false;
      },
      render: (score: number | null | undefined): React.ReactElement => {
        if (score == null) return <>-</>;
        const color = getAttractivenessColor(score);
        return <Tag color={color}>{score}</Tag>;
      },
      width: '5%',
    },
    {
      title: 'Related products',
      dataIndex: 'relatedProductsCount',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        a.relatedProductsCount - b.relatedProductsCount,
      filterSearch: false,
      filters: [
        {
          text: 'With related products',
          value: 'withRelatedProducts',
        },
        {
          text: 'Without related products',
          value: 'withoutRelatedProducts',
        },
        {
          text: 'With one related product',
          value: 'withOneRelatedProduct',
        },
        {
          text: 'With two to five related products',
          value: 'withTwoToFiveRelatedProducts',
        },
        {
          text: 'With more than five related products',
          value: 'withMoreThanFiveRelatedProducts',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withRelatedProducts') {
          return record.relatedProductsCount > 0;
        }

        if (value === 'withoutRelatedProducts') {
          return record.relatedProductsCount === 0;
        }

        if (value === 'withOneRelatedProduct') {
          return record.relatedProductsCount === 1;
        }

        if (value === 'withTwoToFiveRelatedProducts') {
          return (
            record.relatedProductsCount > 1 && record.relatedProductsCount <= 5
          );
        }

        if (value === 'withMoreThanFiveRelatedProducts') {
          return record.relatedProductsCount > 5;
        }

        return false;
      },
      render: coloredRenderOfNumberInTable,
      width: '5%',
    },
    {
      title: (
        <>
          <span>Stocks</span>
          <br />
          <small>internal</small>
          <br />
          <small>external</small>
        </>
      ),
      dataIndex: 'stocks',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        numberCompare(
          a.stocks.internal + a.stocks.external,
          b.stocks.internal + b.stocks.external,
        ),
      render: (stocks: IProductStocks): React.ReactElement => {
        return (
          <Space orientation="vertical" align="start" size={SPACING.sm}>
            <Text>{stocks.internal}</Text>
            <Text>{stocks.external}</Text>
          </Space>
        );
      },
      filterSearch: false,
      filters: [
        {
          text: 'In stock',
          value: 'inStock',
        },
        {
          text: 'Full stock',
          value: 'fullStock',
        },
        {
          text: 'Low stock',
          value: 'lowStock',
        },
        {
          text: 'Out of stock',
          value: 'outOfStock',
        },
        {
          text: 'Only in internal stock',
          value: 'onlyInInternalStock',
        },
        {
          text: 'Only in external stock',
          value: 'onlyInExternalStock',
        },
        {
          text: 'In internal stock',
          value: 'inInternalStock',
        },
        {
          text: 'In external stock',
          value: 'inExternalStock',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        const onStock: number = record.stocks.internal + record.stocks.external;

        if (value === 'inStock') {
          return onStock > 0;
        }

        if (value === 'fullStock') {
          return onStock >= 5;
        }

        if (value === 'lowStock') {
          return onStock > 0 && onStock < 5;
        }

        if (value === 'outOfStock') {
          return onStock === 0;
        }

        if (value === 'onlyInInternalStock') {
          return record.stocks.internal > 0 && record.stocks.external === 0;
        }

        if (value === 'onlyInExternalStock') {
          return record.stocks.external > 0 && record.stocks.internal === 0;
        }

        if (value === 'inInternalStock') {
          return record.stocks.internal > 0;
        }

        if (value === 'inExternalStock') {
          return record.stocks.external > 0;
        }

        return false;
      },
      width: '5%',
    },
    {
      title: 'Primary warehouse',
      dataIndex: 'primaryWarehouse',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        stringCompare(a.primaryWarehouse?.name, b.primaryWarehouse?.name),
      filterSearch: false,
      filters: columnFilterItemPrimaryWarehouse,
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withoutPrimaryWarehouse') {
          return !record.primaryWarehouse;
        }

        if (value === 'withPrimaryWarehouse') {
          return !!record.primaryWarehouse;
        }

        return record.primaryWarehouse?.name === value;
      },
      render: (primaryWarehouse: IWarehouse | undefined): string => {
        return primaryWarehouse ? `${primaryWarehouse.name}` : EMPTY_VALUE;
      },
      width: '10%',
    },
    {
      title: 'Warehouse sync',
      dataIndex: 'productWarehouses',
      filters: [
        {
          text: 'Without sync active',
          value: 'withoutSyncActive',
        },
        {
          text: 'With sync active',
          value: 'withSyncActive',
        },
        {
          text: 'Without sync count',
          value: 'withoutSyncCount',
        },
        {
          text: 'With sync count',
          value: 'withSyncCount',
        },
        {
          text: 'Without sync price',
          value: 'withoutSyncPrice',
        },
        {
          text: 'With sync price',
          value: 'withSyncPrice',
        },
        {
          text: 'Without sync dimensions',
          value: 'withoutSyncDimensions',
        },
        {
          text: 'With sync dimensions',
          value: 'withSyncDimensions',
        },
      ],
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withoutSyncActive') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return !pw.isSyncActive;
            },
          );
        }

        if (value === 'withSyncActive') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return pw.isSyncActive;
            },
          );
        }

        if (value === 'withoutSyncCount') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return !pw.syncCount;
            },
          );
        }

        if (value === 'withSyncCount') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return pw.syncCount;
            },
          );
        }

        if (value === 'withoutSyncPrice') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return !pw.syncPrice;
            },
          );
        }

        if (value === 'withSyncPrice') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return pw.syncPrice;
            },
          );
        }

        if (value === 'withoutSyncDimensions') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return !pw.syncDimensions;
            },
          );
        }

        if (value === 'withSyncDimensions') {
          return record.productWarehouses.some(
            (pw: IProductWarehouseShort): boolean => {
              const warehouse: IWarehouseDetail | undefined =
                Warehouse.warehouses?.find(
                  (w: IWarehouseDetail): boolean =>
                    w.uuid === pw.warehouse.uuid,
                );

              if (warehouse?.isInternal) {
                return false;
              }

              return pw.syncDimensions;
            },
          );
        }

        return false;
      },
      render: (
        productWarehouses: IProductWarehouseShort[],
      ): React.ReactElement => {
        const els: React.ReactElement[] = productWarehouses
          .map((pw: IProductWarehouseShort): React.ReactElement | undefined => {
            const warehouse: IWarehouseDetail | undefined =
              Warehouse.warehouses?.find(
                (w: IWarehouseDetail): boolean => w.uuid === pw.warehouse.uuid,
              );

            if (warehouse?.isInternal) {
              return undefined;
            }

            return (
              <Space
                orientation="horizontal"
                align="start"
                size={SPACING.sm}
                key={pw.warehouse.uuid}
              >
                <Tooltip title="Sync active">
                  <StatusIcon ok={pw.isSyncActive} />
                </Tooltip>
                <Tooltip title="Sync count">
                  <StatusIcon ok={!!pw.syncCount} />
                </Tooltip>
                <Tooltip title="Sync price">
                  <StatusIcon ok={pw.syncPrice} />
                </Tooltip>
                <Tooltip title="Sync dimensions">
                  <StatusIcon ok={pw.syncDimensions} />
                </Tooltip>
                <Tooltip
                  title={`Warehouse: ${
                    warehouse ? warehouse.name : pw.warehouse.uuid
                  }`}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            );
          })
          .filter(notEmpty);

        return (
          <Space orientation="vertical" align="start" size={SPACING.sm}>
            {els}
          </Space>
        );
      },
    },
    {
      title: 'Feature flags',
      dataIndex: 'featureFlags',
      sorter: (a: ProductTableColumn, b: ProductTableColumn): number =>
        a.featureFlags.length - b.featureFlags.length,
      filterSearch: true,
      filters: getTableFeatureFlagsColumnFilters({ dataSource, FeatureFlags }),
      onFilter: (value: boolean | Key, record: ProductTableColumn): boolean => {
        if (value === 'withFeatureFlags') {
          return record.featureFlags.length > 0;
        }

        if (value === 'withoutFeatureFlags') {
          return record.featureFlags.length === 0;
        }

        return record.featureFlags.some(
          (productFeatureFlag: IProductFeatureFlag): boolean => {
            return productFeatureFlag.code === value;
          },
        );
      },
      render: (
        productFeatureFlags: IProductFeatureFlag[],
      ): React.ReactElement[] =>
        featureFlagTags({ productFeatureFlags, FeatureFlags, Warehouse }),
      width: '5%',
    },
  ];

  return columns;
}
